import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  IHealthCheckAccessConfig,
  IHealthCheckAccessStatus,
} from '../../Interfaces/IHealthCheckAccess';
import { customFetcher } from '../useFetch';

const CONFIG_KEY = ['healthcheck-access', 'config'];
const STATUS_KEY = ['healthcheck-access', 'status'];

/**
 * Ist der Health Check für den angemeldeten Benutzer freigeschaltet (#1283)?
 * Ohne Freischaltung kein Check beim Speichern, kein Dialog, kein Knopf im
 * Vorgang und kein Vorgang-Check in den Systemeinstellungen. Solange die Antwort
 * fehlt, gilt: aus.
 */
export const useGetHealthCheckStatus = () => {
  const { data, isLoading: isLoadingHealthCheckStatus } =
    useQuery<IHealthCheckAccessStatus>({
      queryKey: STATUS_KEY,
      queryFn: () => customFetcher({ endpoint: '/healthcheck-access/status' }),
      refetchOnWindowFocus: false,
    });

  return {
    isHealthCheckEnabled: data?.Enabled === true,
    isLoadingHealthCheckStatus,
  };
};

/** Hauptschalter und freigeschaltete Benutzer — nur mit Entwickler-Zugang (`dev`). */
export const useGetHealthCheckAccessConfig = () => {
  const {
    data: healthCheckAccessConfig,
    isLoading: isLoadingHealthCheckAccessConfig,
  } = useQuery<IHealthCheckAccessConfig>({
    queryKey: CONFIG_KEY,
    queryFn: () => customFetcher({ endpoint: '/healthcheck-access/config' }),
  });

  return { healthCheckAccessConfig, isLoadingHealthCheckAccessConfig };
};

export const useSaveHealthCheckAccessConfig = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: saveHealthCheckAccessConfig,
    isPending: isPendingSaveHealthCheckAccessConfig,
  } = useMutation({
    mutationFn: (uploadObject: Partial<IHealthCheckAccessConfig>) =>
      customFetcher({
        endpoint: '/healthcheck-access/config',
        body: uploadObject,
        method: 'PUT',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONFIG_KEY });
      // Wer sich selbst freischaltet, sieht den Health Check sofort.
      queryClient.invalidateQueries({ queryKey: STATUS_KEY });
    },
  });

  return {
    saveHealthCheckAccessConfig,
    isPendingSaveHealthCheckAccessConfig,
  };
};
